import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Event, NavigationEnd, Router } from "@angular/router";
import { ChipItem, ChipModule } from "@storeblocks/chip-ng";

import { BehaviorSubject, fromEvent, Observable } from "rxjs";
import { filter, map } from "rxjs/operators";
import { RouteKey, routes } from "src/app/pages/articles-page/routes";
import { FmsService } from "src/app/services/fms.service";
import { filterUnique } from "src/app/utils/array";
import { getIsNotNullable, hasShadowDom, removeQueryParams } from "src/app/utils/utils";
import { AlertModule } from "@storeblocks/alert-ng";
import { ArticlesFaqComponent } from "./components/articles-faq/articles-faq.component";
import { AsyncPipe } from "@angular/common";
import { CardNavModule } from "@storeblocks/card-nav-ng";
import { smartPensionBaseUrl } from "src/app/constants/api.constants";
import { PageTitleComponent } from "src/app/modules/shared/components/page-title/page-title.component";
import { FmsPipe } from "src/app/modules/shared/pipes/fms.pipe";
import { CardComponent } from "src/app/components/card/card.component";

// Angular Router automatically scrolls to the top of the page
// on navigation. The scroll event fires somewhere between 0 and
// <the value below> milliseconds after navigation. Use this value
// to delay custom scrolling.
const NATIVE_SCROLL_WINDOW = 20;

export interface DynamicArticle {
  key: string;
  url: string;
  title: string;
  enabled: boolean;
  external: boolean;
  featured: boolean;
  tags?: string[];
  readingTime?: number;
  imageUrl: string;
  openInNewTab?: boolean;
  route?: string;
}

@Component({
  selector: "app-articles-overview-page",
  templateUrl: "./articles-page.component.html",
  styleUrls: ["./articles-page.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PageTitleComponent,
    ChipModule,
    FormsModule,
    ReactiveFormsModule,
    AlertModule,
    ArticlesFaqComponent,
    AsyncPipe,
    FmsPipe,
    CardNavModule,
    CardComponent,
  ],
})
export class ArticlesPageComponent {
  private lastScrollPosition = 0;
  private readonly ALL_TAG = "all";
  private readonly selectedTag$ = new BehaviorSubject<string>(this.ALL_TAG);

  readonly fmsService = inject(FmsService);
  readonly router = inject(Router);

  public readonly enabledArticles: DynamicArticle[];
  public readonly articles$!: Observable<DynamicArticle[]>;

  public readonly articles = new FormControl<ChipItem[]>([]);
  public readonly isEnglish = this.fmsService.getLocale() === "en";

  constructor() {
    this.scrollToLastPosition();
    this.saveLastScrollPosition();

    const articles = this.fmsService.getJSON<DynamicArticle[]>("articles.dynamicArticles");
    this.enabledArticles = getEnabledArticles(articles).map((article) => {
      const url = this.getUrl(article);
      const openInNewTab = !this.shouldOpenInternally(article);
      const route = openInNewTab ? "" : this.getUrl(article, false);
      return { ...article, url, openInNewTab, route };
    });

    this.articles$ = this.selectedTag$.pipe(
      map((selectedTag) =>
        this.enabledArticles.filter(
          (article) => selectedTag === this.ALL_TAG || article.tags?.some((tag) => tag === selectedTag),
        ),
      ),
    );

    this.initArticleFormControl();
  }

  public setFilter(selectedTag: ChipItem): void {
    this.selectedTag$.next(String(selectedTag.value));
  }

  private getUrl(article: DynamicArticle, withBaseUrl = true): string {
    const { key, url, external } = article;

    if (this.shouldOpenInternally(article)) {
      if (external) {
        const baseUrlPrefix = withBaseUrl ? smartPensionBaseUrl : "";
        return `${baseUrlPrefix}/${routes[RouteKey.Root]}/${routes[RouteKey.Article]}/${key}`;
      }
      return url;
    }

    return removeQueryParams(url, ["headless=true"]);
  }

  private shouldOpenInternally(article: DynamicArticle): boolean {
    return (hasShadowDom() && !!article.key) || (!article.external && !!article.url);
  }

  private async initArticleFormControl(): Promise<void> {
    const allItemsLabel = this.fmsService.getText("articles.articlesOverview.allArticles");
    const initialChipItem: ChipItem = {
      id: this.ALL_TAG,
      label: allItemsLabel,
      selected: true,
      trackId: "all-articles",
      value: this.ALL_TAG,
    };

    const tags = getTagsFromArticles(this.enabledArticles);
    const tagChipItems = tags.map((tag, index) => ({
      id: tag,
      label: tag,
      selected: false,
      trackId: `articles-filter-${index + 1}`,
      value: tag,
    }));

    this.articles.setValue([initialChipItem, ...tagChipItems]);
  }

  private scrollToLastPosition(): void {
    this.router.events.pipe(filter(isComponentNavigationUrl)).subscribe(() => {
      const lastScrollPosition = this.lastScrollPosition;
      setTimeout(() => window.scrollTo(0, lastScrollPosition), NATIVE_SCROLL_WINDOW);
    });
  }

  private saveLastScrollPosition(): void {
    fromEvent(window, "scroll")
      .pipe(filter(() => isComponentUrl(this.router.url)))
      .subscribe(() => {
        this.lastScrollPosition = window.scrollY;
      });
  }
}

function getEnabledArticles(articles: DynamicArticle[]): DynamicArticle[] {
  return articles.filter((article) => article.enabled);
}

function getTagsFromArticles(articles: DynamicArticle[]): string[] {
  const allTags = articles.flatMap(({ tags }) => tags).filter(getIsNotNullable);

  return filterUnique(allTags);
}

function isComponentUrl(url: string): boolean {
  return url === `/${routes[RouteKey.Root]}`;
}

function isComponentNavigationUrl(event: Event): boolean {
  return event instanceof NavigationEnd && isComponentUrl(event.url);
}
